import { Icon } from '@starter/ui';
import { createFileRoute, Link, Outlet, redirect, useRouter } from '@tanstack/react-router';
import { z } from 'zod';
import { authClient, useSession } from '../lib/auth.ts';

/**
 * `/account` — three destinations behind one nav, which is the shape the
 * original has and this screen did not.
 *
 * It was one long page of five cards: everything the template's auth library
 * can do, stacked. The product's is a 200-wide list of three — profile,
 * subscription, help — beside a 720-wide body, with signing out and reaching
 * support at the foot of the list rather than inside a page
 * (`prd/measured/account.json`, read out of the bundle: `accountNavW` 200,
 * `accountNavItemH` 48, `accountBodyW` 720).
 *
 * The forms did not change; where they *are* did. A person looking for their
 * password now has one place to look rather than a scroll.
 *
 * ## The list is the brand editor's list
 *
 * Same shape, same numbers: a pill 48 high in a column 200 wide, filled
 * `brand-200` when it is the one you are on, label 16px/22.4px — which is what
 * `routes/brands.$brandId.tsx` draws and what this drew differently for no
 * measured reason. Radius and fill were never read off the original; only the
 * column's 200 and the item's 48 were (`prd/measured/account.json`), and both
 * match the brand nav exactly, so adopting it changes nothing the gate holds.
 *
 * **The column sticks and the destinations stay three.** The owner asked for
 * the brand editor's scroll behaviour on 2026-09-26 and chose the half that
 * agrees with the original: `accountNavW` is annotated «a list of three, not
 * tabs», so merging these into one scrolling page would be a divergence rather
 * than a fix. What was actually missing is that the list rode the page up and
 * out of sight — `lg:sticky` is the whole of it.
 */
const LINKS = [
  { to: '/account/profile', label: 'Profile', icon: 'shield-check' },
  { to: '/account/subscription', label: 'Subscription', icon: 'sparkles' },
  { to: '/account/help', label: 'Help & legal', icon: 'info' },
] as const;

function AccountLayout() {
  const router = useRouter();
  const { data: session } = useSession();

  return (
    <div className="flex flex-col gap-6">
      <h1
        className="font-display text-[28px] leading-[34px] font-bold tracking-[-0.7px] text-ink"
        data-testid="account-title"
      >
        Account
      </h1>

      <div className="flex flex-wrap items-start gap-10">
        <nav
          className="flex h-fit w-50 flex-none flex-col gap-0.5 lg:sticky lg:top-4"
          data-fidelity="account-nav"
        >
          {LINKS.map((link) => (
            <Link
              activeProps={{
                'data-current': 'yes',
                className: 'bg-brand-200 font-semibold',
              }}
              className="flex h-12 items-center gap-2 rounded-full px-5 text-left text-[16px] leading-[22.4px] font-normal text-ink hover:bg-surface-muted"
              data-fidelity="account-nav-item"
              data-testid={`account-nav-${link.label.split(' ')[0]?.toLowerCase()}`}
              key={link.to}
              to={link.to}
            >
              <Icon className="text-ink-muted" name={link.icon} size={18} />
              {link.label}
            </Link>
          ))}

          {/*
            Signing out is in the nav rather than on a page, because it belongs
            to the account and not to any one part of it — the original puts it
            in the same list.
          */}
          <button
            className="mt-2 flex h-12 cursor-pointer items-center gap-2 rounded-full px-5 text-left text-[16px] leading-[22.4px] text-ink-muted hover:bg-surface-muted hover:text-ink"
            data-testid="account-sign-out"
            onClick={async () => {
              await authClient.signOut();
              await router.navigate({ to: '/login', search: {} });
              await router.invalidate();
            }}
            type="button"
          >
            <Icon name="log-out" size={18} />
            Log out
          </button>
        </nav>

        <div className="min-w-0 flex-1 md:max-w-[720px]" data-fidelity="account-body">
          {session ? <Outlet /> : <p className="text-sm text-ink-muted">Loading…</p>}
        </div>
      </div>
    </div>
  );
}

export const Route = createFileRoute('/account')({
  validateSearch: z.object({
    /** Better Auth appends this when an email-change link is stale. */
    error: z.string().optional(),
  }),
  beforeLoad: async ({ location }) => {
    const { data } = await authClient.getSession();
    if (!data) {
      throw redirect({ to: '/login', search: { redirectTo: location.href } });
    }
  },
  component: AccountLayout,
});
